// ======================================================================
// File: src/modules/ipe/engineering/bom/EngineeringBOMCard.js
// ======================================================================

import React, { useEffect, useState } from "react";
import ipeEnquiryService from "../../services/ipeEnquiryService";
import BOMLinesTable from "./BOMLinesTable";
import axiosClient from "../../../../api/axiosClient";  

// ------------------------------------------------------------------
const BOM_STATUS_MAP = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

export default function EngineeringBOMCard({ item, onBOMCreated, materialMap  }) {

  const [bom, setBom] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [materialMapState, setMaterialMapState] = useState({});
  // ------------------------------------------------------------
  async function loadBOM() {

    console.log("🔄 Loading BOM for item:", item.id);

    setLoading(true);

    try {

      const res = await ipeEnquiryService.getActiveBOM(item.id);

      console.log("📦 BOM RESPONSE:", res);

      const bomData = res || null;

      setBom(bomData);

      if (bomData?.id) {

        const l = await ipeEnquiryService.listBOMLines(bomData.id);

        console.log("📋 BOM LINES:", l);

        setLines(l || []);

      } else {

        console.log("⚠️ No BOM found for item:", item.id);

        setLines([]);

      }

    } catch (err) {

      console.error("❌ Error loading BOM:", err);

    } finally {

      setLoading(false);

    }

  }

  useEffect(() => {
    loadBOM();
  }, [item.id]);

  // ------------------------------------------------------------
  function resolveVariantDisplay(item) {
    if (item.product_variant_code && item.product_variant_name) {
      return `${item.product_variant_code} – ${item.product_variant_name}`;
    }
    if (item.product_variant_name) return item.product_variant_name;
    if (item.product_variant_code) return item.product_variant_code;

    return "Variant Not Defined";
  }

  // ------------------------------------------------------------

  const statusCode = bom?.status?.toUpperCase();

  const statusLabel = statusCode
    ? BOM_STATUS_MAP[statusCode]
    : null;

  const isDraft = statusCode === "DRAFT";
  const isSubmitted = statusCode === "SUBMITTED";
  const isRejected = statusCode === "REJECTED";
  const isFrozen = false;

  
  useEffect(() => {
    async function loadMaterials() {
      try {
        const res = await axiosClient.get("/api/ppe/masters/raw-materials/all");

        const materials = res.data?.data || [];

        const map = {};
        materials.forEach(m => {
          map[String(m.id)] = m.material_name || m.name;
        });

        console.log("🧠 MATERIAL MAP (CARD):", map);

        setMaterialMapState(map);

      } catch (err) {
        console.error("Failed to load materials", err);
      }
    }

    loadMaterials();
  }, []);
  
  // Auto expand draft
  useEffect(() => {
    if (bom) setExpanded(isDraft);
  }, [bom?.id]);

  // ------------------------------------------------------------

  async function safeAction(fn) {

    try {

      setActionLoading(true);

      await fn();

      await loadBOM();

    } catch (err) {

      console.error("❌ Action error:", err);

    } finally {

      setActionLoading(false);

    }

  }

  // ------------------------------------------------------------

  return (
    <div className="card mb-3">

      {/* ================= HEADER ================= */}

      <div
        className="card-header"
        style={{ cursor: bom ? "pointer" : "default" }}
        onClick={() => bom && setExpanded((e) => !e)}
      >

        <div className="d-flex justify-content-between align-items-start">

          <div>

            <strong>
              {expanded ? "▼" : "▶"}{" "}
              {resolveVariantDisplay(item)}
            </strong>

            <div className="text-muted small">
              Item {item.item_seq_no} | Qty: {item.quantity}
              {item.equipment_name && ` | Equipment: ${item.equipment_name}`}
            </div>

            {bom && (
              <div className="mt-1">

                <span className="badge bg-secondary me-1">
                  Version {bom.version_no}
                </span>

                <span className="badge bg-info me-1">
                  {statusLabel}
                </span>

                {isFrozen && (
                  <span className="badge bg-danger">
                    Frozen
                  </span>
                )}

              </div>
            )}

          </div>

          {/* ================= CREATE BOM ================= */}
          {!bom && (
            <button
              className="btn btn-sm btn-primary"
              onClick={(e) => {
                e.stopPropagation();

                console.log("🟢 Creating BOM for item:", item.id);

                safeAction(async () => {

                  const res = await ipeEnquiryService.createBOM({
                    enquiry_item_id: item.id,
                    equipment_id: item.equipment_id
                  });

                  console.log("✅ BOM CREATED:", res);

                  // 🔥 CRITICAL FIX
                  if (onBOMCreated) {
                    console.log("🔄 Triggering parent BOM reload...");
                    await onBOMCreated();
                  }

                });

              }}
              disabled={actionLoading}
            >
              + Create BOM
            </button>
          )}

        </div>

      </div>

      {/* ================= BODY ================= */}

      {expanded && bom && (

        <div className="card-body">

          <div
            className="d-flex justify-content-end mb-2"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="btn-group btn-group-sm">

              {isDraft && !isFrozen && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() =>
                    safeAction(() =>
                      ipeEnquiryService.submitBOM(bom.id)
                    )
                  }
                  disabled={actionLoading}
                >
                  Submit
                </button>
              )}

              {isSubmitted && (
                <>
                  <button
                    className="btn btn-outline-success"
                    onClick={() =>
                      safeAction(() =>
                        ipeEnquiryService.approveBOM(bom.id)
                      )
                    }
                    disabled={actionLoading}
                  >
                    Approve
                  </button>

                  <button
                    className="btn btn-outline-danger"
                    onClick={() => {

                      const reason = prompt("Enter rejection reason");

                      if (reason)
                        safeAction(() =>
                          ipeEnquiryService.rejectBOM(
                            bom.id,
                            reason
                          )
                        );

                    }}
                    disabled={actionLoading}
                  >
                    Reject
                  </button>
                </>
              )}

              {isRejected && (
                <button
                  className="btn btn-outline-warning"
                  onClick={() =>
                    safeAction(() =>
                      ipeEnquiryService.replanBOM(bom.id)
                    )
                  }
                  disabled={actionLoading}
                >
                  Replan BOM
                </button>
              )}

            </div>

          </div>

          <BOMLinesTable
            lines={lines}
            bomId={bom?.id}
            canEdit={isDraft && !isFrozen}
            onReload={loadBOM}
             materialMap={materialMapState}  // 🔥 ADD THIS
          />

        </div>

      )}

    </div>
  );

}